import { useMemo, useCallback, useEffect, useState } from "react";
import {
  User,
  Users,
  ClipboardList,
  MapPin,
  DollarSign,
  Ruler,
  CheckCircle,
  Briefcase,
  Phone,
} from "lucide-react";
import { SlidePanel } from "@/components/ui/slide-panel";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ExtendedProject } from "@/types/project";
import { supabase } from "@/lib/supabaseClient";
import { installerPartnerOptions, type InstallerPartnerRow } from "@/lib/installerPartnerOptions";
import type { LucideIcon } from "lucide-react";
import type {
  AssignmentType,
  AssignmentUser,
  PanelStates,
  SearchStates,
} from "@/hooks/useProjectAssignments";

// ============================================================================
// Types
// ============================================================================

/** Configuration for each assignment type */
interface AssignmentConfig {
  type: AssignmentType;
  title: string;
  icon: LucideIcon;
  entityType: "user" | "partner";
  selectionMode: "single" | "multi";
  placeholder: string;
  emptyMessage: string;
  roleFilter?: number[]; // Filter users by role_id
  useEligibleUsers?: boolean; // Use eligibleUsers instead of allUsers
  currentValueField: string; // Field in project to check for current value
}

/** Props for the main ProjectAssignmentPanels component */
interface ProjectAssignmentPanelsProps {
  // From useProjectAssignments hook
  panelStates: PanelStates;
  searchStates: SearchStates;
  setSearch: (type: AssignmentType, value: string) => void;
  closePanel: (type: AssignmentType) => void;

  // Current project (for current values)
  project: ExtendedProject | null;

  // Available options
  allUsers: AssignmentUser[];
  eligibleUsers: AssignmentUser[];
  followers: AssignmentUser[];

  // Assignment handlers
  handleChangeOwner: (userId: string) => Promise<void>;
  handleChangeManager: (userId: string) => Promise<void>;
  handleChangeProposalDesigner: (userId: string) => Promise<void>;
  handleChangeInstallerPartner: (partnerId: string) => Promise<void>;
  handleChangeInstallerPM: (userId: string) => Promise<void>;
  handleChangeSurveyor: (userId: string) => Promise<void>;
  handleChangeBizDev: (userId: string) => Promise<void>;
  handleChangeCloser: (userId: string) => Promise<void>;
  handleChangeSetter: (userId: string) => Promise<void>;

  // Follower handlers
  addFollower: (userId: string) => Promise<void>;
  removeFollower: (userId: string) => Promise<void>;
}

/** Props for individual assignment panel */
interface AssignmentPanelProps {
  config: AssignmentConfig;
  isOpen: boolean;
  onClose: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  currentValue: string | null;
  users: AssignmentUser[];
  onSelect: (userId: string) => void;
  /** The project's account; the installer list is scoped to it. */
  tenantId?: string | null;
}

/** Props for followers panel (special multi-select behavior) */
interface FollowersPanelProps {
  isOpen: boolean;
  onClose: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  allUsers: AssignmentUser[];
  followers: AssignmentUser[];
  onAdd: (userId: string) => void;
  onRemove: (userId: string) => void;
}

// ============================================================================
// Constants
// ============================================================================

/** Common SlidePanel props for all assignment panels */
const COMMON_PANEL_PROPS = {
  width: "md" as const,
  position: "right" as const,
  showBackdrop: true,
  closeOnBackdropClick: true,
  topOffset: "calc(4rem + 10px)",
  panelClassName: "border-l-2 border-t-2 border-border rounded-tl-lg",
  backdropClassName: "!left-0",
  immediateBackdrop: true,
  showCloseButton: true,
  zIndex: 220,
};

/** Configuration for each assignment type */
const ASSIGNMENT_CONFIGS: Record<
  Exclude<AssignmentType, "followers">,
  AssignmentConfig
> = {
  owner: {
    type: "owner",
    title: "Change Project Owner",
    icon: User,
    entityType: "user",
    selectionMode: "single",
    placeholder: "Search users...",
    emptyMessage: "No users found",
    currentValueField: "owner.id",
  },
  manager: {
    type: "manager",
    title: "Change Project Manager",
    icon: ClipboardList,
    entityType: "user",
    selectionMode: "single",
    placeholder: "Search users...",
    emptyMessage: "No users found",
    currentValueField: "assigned_internal_pm_id",
  },
  proposal_designer: {
    type: "proposal_designer",
    title: "Change Proposal Designer",
    icon: Ruler,
    entityType: "user",
    selectionMode: "single",
    placeholder: "Search users...",
    emptyMessage: "No users found",
    currentValueField: "assigned_proposal_designer_id",
  },
  installer_partner: {
    type: "installer_partner",
    title: "Change Installation Partner",
    icon: Briefcase,
    entityType: "partner",
    selectionMode: "single",
    placeholder: "Search partners...",
    emptyMessage: "No installer companies yet. Add one under Settings > Users > Partners.",
    currentValueField: "assigned_installer_partner_id",
  },
  installer_pm: {
    type: "installer_pm",
    title: "Change Installer Project Manager",
    icon: Users,
    entityType: "user",
    selectionMode: "single",
    placeholder: "Search users...",
    emptyMessage: "No users found",
    roleFilter: [5, 6], // Installer roles
    currentValueField: "assigned_installer_pm_id",
  },
  surveyor: {
    type: "surveyor",
    title: "Change Site Surveyor",
    icon: MapPin,
    entityType: "user",
    selectionMode: "single",
    placeholder: "Search users...",
    emptyMessage: "No users found",
    roleFilter: [21], // Site surveyor role
    currentValueField: "assigned_site_surveyor_id",
  },
  biz_dev: {
    type: "biz_dev",
    title: "Change Business Development Rep",
    icon: DollarSign,
    entityType: "user",
    selectionMode: "single",
    placeholder: "Search users...",
    emptyMessage: "No users found",
    roleFilter: [5, 6, 21], // Biz dev eligible roles
    currentValueField: "assigned_biz_dev_rep_id",
  },
  closer: {
    type: "closer",
    title: "Change Assigned Closer",
    icon: CheckCircle,
    entityType: "user",
    selectionMode: "single",
    placeholder: "Search users...",
    emptyMessage: "No users found",
    useEligibleUsers: true, // Uses team-based filtering
    currentValueField: "assigned_closer_id",
  },
  setter: {
    type: "setter",
    title: "Change Assigned Setter",
    icon: Phone,
    entityType: "user",
    selectionMode: "single",
    placeholder: "Search users...",
    emptyMessage: "No users found",
    useEligibleUsers: true, // Uses team-based filtering
    currentValueField: "assigned_setter_id",
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/** Get initials from user for avatar fallback */
function getInitials(user: AssignmentUser): string {
  const first = user.first_name?.charAt(0) || "";
  const last = user.last_name?.charAt(0) || "";
  return (first + last).toUpperCase() || "?";
}

/** Get current value from project based on field path */
function getCurrentValue(
  project: ExtendedProject | null,
  fieldPath: string
): string | null {
  if (!project) return null;

  // Handle nested paths like "owner.id"
  const parts = fieldPath.split(".");
  let value: unknown = project;

  for (const part of parts) {
    if (value && typeof value === "object" && part in value) {
      value = (value as Record<string, unknown>)[part];
    } else {
      return null;
    }
  }

  return typeof value === "string" ? value : null;
}

/** Filter users by search term */
function filterUsersBySearch(
  users: AssignmentUser[],
  searchTerm: string
): AssignmentUser[] {
  if (!searchTerm) return users;

  const term = searchTerm.toLowerCase();
  return users.filter(
    (u) =>
      u &&
      u.first_name &&
      u.last_name &&
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(term)
  );
}

/** Filter users by role */
function filterUsersByRole(
  users: AssignmentUser[],
  roleFilter?: number[]
): AssignmentUser[] {
  if (!roleFilter || roleFilter.length === 0) return users;

  return users.filter((u) => {
    if (!u || u.role_id === undefined) return false;
    const roleId =
      typeof u.role_id === "string" ? parseInt(u.role_id, 10) : u.role_id;
    return roleFilter.includes(roleId);
  });
}

// ============================================================================
// Sub-Components
// ============================================================================

/** User selection item in assignment panels */
function UserSelectionItem({
  user,
  isSelected,
  onSelect,
}: {
  user: AssignmentUser;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors hover:bg-muted ${
        isSelected ? "bg-primary/10 border border-primary/20" : ""
      }`}
      onClick={onSelect}
    >
      <Avatar className="h-8 w-8 mr-3">
        {user.avatar_url ? (
          <AvatarImage src={user.avatar_url} alt={getInitials(user)} />
        ) : (
          <AvatarFallback className="text-xs">{getInitials(user)}</AvatarFallback>
        )}
      </Avatar>
      <div className="flex-1">
        <div className="font-medium">
          {user.first_name || ""} {user.last_name || ""}
        </div>
        <div className="text-xs text-muted-foreground">{user.email || ""}</div>
      </div>
      {isSelected && (
        <span className="text-xs text-primary font-semibold">CURRENT</span>
      )}
    </div>
  );
}

/** Single assignment panel (for owner, manager, etc.) */
function AssignmentPanel({
  config,
  isOpen,
  onClose,
  searchValue,
  onSearchChange,
  currentValue,
  users,
  onSelect,
  tenantId,
}: AssignmentPanelProps) {
  // Filter users by role if specified
  const roleFilteredUsers = useMemo(
    () => filterUsersByRole(users, config.roleFilter),
    [users, config.roleFilter]
  );

  // Filter by search
  const filteredUsers = useMemo(
    () => filterUsersBySearch(roleFilteredUsers, searchValue),
    [roleFilteredUsers, searchValue]
  );

  const handleClose = useCallback(() => {
    onSearchChange("");
    onClose();
  }, [onSearchChange, onClose]);

  const handleSelect = useCallback(
    (userId: string) => {
      onSelect(userId);
      onSearchChange("");
    },
    [onSelect, onSearchChange]
  );

  if (config.type === "installer_partner") {
    return (
      <InstallerPartnerPanel
        config={config}
        isOpen={isOpen}
        onClose={handleClose}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        currentValue={currentValue}
        onSelect={handleSelect}
        tenantId={tenantId ?? null}
      />
    );
  }

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={config.title}
      {...COMMON_PANEL_PROPS}
    >
      <div className="mb-4">
        <input
          type="text"
          placeholder={config.placeholder}
          className="w-full border rounded px-3 py-2 mb-3"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          autoFocus
        />
        <div className="space-y-2">
          {filteredUsers.map((user) => (
            <UserSelectionItem
              key={user.id}
              user={user}
              isSelected={user.id === currentValue}
              onSelect={() => handleSelect(user.id)}
            />
          ))}
          {filteredUsers.length === 0 && (
            <div className="text-muted-foreground text-sm p-3 text-center">
              {config.emptyMessage}
            </div>
          )}
        </div>
      </div>
    </SlidePanel>
  );
}

/** Installation partner panel: the account's installer-type partner companies. */
function InstallerPartnerPanel({
  config,
  isOpen,
  onClose,
  searchValue,
  onSearchChange,
  currentValue,
  onSelect,
  tenantId,
}: {
  config: AssignmentConfig;
  isOpen: boolean;
  onClose: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  currentValue: string | null;
  onSelect: (partnerId: string) => void;
  tenantId: string | null;
}) {
  const [rows, setRows] = useState<InstallerPartnerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!isOpen || !tenantId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    supabase
      .from("installer_partners")
      .select("id, company_name, city, state, is_active, partner_type")
      .eq("tenant_id", tenantId)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("Failed to load installer companies:", error);
          setLoadError(true);
        }
        setRows((data as InstallerPartnerRow[] | null) ?? []);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, tenantId]);

  const options = useMemo(
    () => installerPartnerOptions(rows, searchValue, currentValue),
    [rows, searchValue, currentValue]
  );

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title={config.title} {...COMMON_PANEL_PROPS}>
      <div className="mb-4">
        <input
          type="text"
          aria-label="Search installer companies"
          placeholder={config.placeholder}
          className="w-full border rounded px-3 py-2 mb-3"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          autoFocus
        />
        <div className="space-y-2">
          {loading && <div className="text-muted-foreground text-sm p-3 text-center">Loading…</div>}
          {!loading && loadError && (
            <div className="text-destructive text-sm p-3 text-center">Could not load installer companies.</div>
          )}
          {!loading && !loadError && (
            <>
              {currentValue && !searchValue && (
                <button
                  type="button"
                  onClick={() => onSelect("none")}
                  className="w-full rounded-md border border-dashed border-border px-3 py-2.5 text-left text-sm text-muted-foreground hover:bg-muted"
                >
                  No installer
                </button>
              )}
              {options.map((o) => {
                const selected = o.id === currentValue;
                return (
                  <button
                    key={o.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onSelect(o.id)}
                    className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
                      selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
                    }`}
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      <Briefcase className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground">{o.name}</span>
                        {o.location && (
                          <span className="block truncate text-xs text-muted-foreground">{o.location}</span>
                        )}
                      </span>
                    </span>
                    {selected && <CheckCircle className="h-4 w-4 shrink-0 text-primary" aria-label="Current installer" />}
                  </button>
                );
              })}
              {options.length === 0 && (
                <div className="text-muted-foreground text-sm p-3 text-center">
                  {searchValue ? "No installer companies match." : config.emptyMessage}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </SlidePanel>
  );
}

/** Followers panel with add/remove functionality */
function FollowersPanel({
  isOpen,
  onClose,
  searchValue,
  onSearchChange,
  allUsers,
  followers,
  onAdd,
  onRemove,
}: FollowersPanelProps) {
  const handleClose = useCallback(() => {
    onSearchChange("");
    onClose();
  }, [onSearchChange, onClose]);

  // Filter followers by search
  const filteredFollowers = useMemo(() => {
    if (!searchValue) return followers;
    const term = searchValue.toLowerCase();
    return followers.filter(
      (f) =>
        f &&
        f.first_name &&
        f.last_name &&
        f.email &&
        (f.first_name + " " + f.last_name + " " + f.email)
          .toLowerCase()
          .includes(term)
    );
  }, [followers, searchValue]);

  // Filter available users (not already followers)
  const availableUsers = useMemo(() => {
    const followerIds = new Set(followers.map((f) => f.id));
    let available = allUsers.filter(
      (u) => u && u.first_name && u.last_name && u.email && !followerIds.has(u.id)
    );

    if (searchValue) {
      const term = searchValue.toLowerCase();
      available = available.filter(
        (u) =>
          (u.first_name + " " + u.last_name + " " + u.email)
            .toLowerCase()
            .includes(term)
      );
    }

    return available;
  }, [allUsers, followers, searchValue]);

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Manage Followers"
      {...COMMON_PANEL_PROPS}
    >
      <div className="flex flex-col h-full">
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search users..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full border rounded px-3 py-2"
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Current Followers Section */}
          {followers.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wider">
                Current Followers ({followers.length})
              </h4>
              <div className="space-y-1">
                {filteredFollowers.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center">
                      <Avatar className="h-8 w-8 mr-3">
                        {f.avatar_url ? (
                          <AvatarImage src={f.avatar_url} alt={getInitials(f)} />
                        ) : (
                          <AvatarFallback className="text-xs">
                            {getInitials(f)}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div>
                        <div className="font-medium">
                          {f.first_name || ""} {f.last_name || ""}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {f.email || ""}
                        </div>
                      </div>
                    </div>
                    <button
                      className="px-3 py-1.5 text-sm bg-destructive/10 text-destructive rounded-md hover:bg-destructive/20 transition-colors"
                      onClick={() => onRemove(f.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Users Section */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wider">
              Available Users
            </h4>
            <div className="space-y-1">
              {availableUsers.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="flex items-center">
                    <Avatar className="h-8 w-8 mr-3">
                      {u.avatar_url ? (
                        <AvatarImage src={u.avatar_url} alt={getInitials(u)} />
                      ) : (
                        <AvatarFallback className="text-xs">
                          {getInitials(u)}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div>
                      <div className="font-medium">
                        {u.first_name || ""} {u.last_name || ""}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {u.email || ""}
                      </div>
                    </div>
                  </div>
                  <button
                    className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                    onClick={() => onAdd(u.id)}
                  >
                    Add
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SlidePanel>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * ProjectAssignmentPanels - Consolidated component for all assignment slide panels
 *
 * Handles 10 assignment types through configuration:
 * - owner: Project owner
 * - manager: Project manager
 * - proposal_designer: Proposal designer
 * - installer_partner: Installation partner
 * - installer_pm: Installer project manager
 * - surveyor: Site surveyor
 * - biz_dev: Business development rep
 * - closer: Assigned closer
 * - setter: Assigned setter
 * - followers: Project followers (multi-select)
 */
export function ProjectAssignmentPanels({
  panelStates,
  searchStates,
  setSearch,
  closePanel,
  project,
  allUsers,
  eligibleUsers,
  followers,
  handleChangeOwner,
  handleChangeManager,
  handleChangeProposalDesigner,
  handleChangeInstallerPartner,
  handleChangeInstallerPM,
  handleChangeSurveyor,
  handleChangeBizDev,
  handleChangeCloser,
  handleChangeSetter,
  addFollower,
  removeFollower,
}: ProjectAssignmentPanelsProps) {
  // Map assignment types to their handlers
  const handlers: Record<Exclude<AssignmentType, "followers">, (id: string) => Promise<void>> = {
    owner: handleChangeOwner,
    manager: handleChangeManager,
    proposal_designer: handleChangeProposalDesigner,
    installer_partner: handleChangeInstallerPartner,
    installer_pm: handleChangeInstallerPM,
    surveyor: handleChangeSurveyor,
    biz_dev: handleChangeBizDev,
    closer: handleChangeCloser,
    setter: handleChangeSetter,
  };

  return (
    <>
      {/* Followers Panel - Special multi-select behavior */}
      <FollowersPanel
        isOpen={panelStates.followers}
        onClose={() => closePanel("followers")}
        searchValue={searchStates.followers}
        onSearchChange={(value) => setSearch("followers", value)}
        allUsers={allUsers}
        followers={followers}
        onAdd={addFollower}
        onRemove={removeFollower}
      />

      {/* Single-select assignment panels */}
      {(Object.keys(ASSIGNMENT_CONFIGS) as Array<Exclude<AssignmentType, "followers">>).map(
        (type) => {
          const config = ASSIGNMENT_CONFIGS[type];
          const users = config.useEligibleUsers ? eligibleUsers : allUsers;

          return (
            <AssignmentPanel
              key={type}
              config={config}
              isOpen={panelStates[type]}
              onClose={() => closePanel(type)}
              searchValue={searchStates[type]}
              onSearchChange={(value) => setSearch(type, value)}
              currentValue={getCurrentValue(project, config.currentValueField)}
              users={users}
              tenantId={project?.tenant_id ?? null}
              onSelect={handlers[type]}
            />
          );
        }
      )}
    </>
  );
}

ProjectAssignmentPanels.displayName = "ProjectAssignmentPanels";
